import { graphHooks } from '@tunasong/graph-lib/react'
import { ThumbsDown, ThumbsUp } from '@tunasong/icons'
import type { OptionRating, PollOption } from '@tunasong/schemas'
import { IconButton } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { getOptionLabel } from './option-label.js'
import css from './option-rating.module.css'

export interface OptionRatingButtonsProps {
  option: PollOption
  rating?: OptionRating
  /** More per-option controls (the comment button), in the same labelled group. */
  children?: ReactNode
  onChange(rating: OptionRating | undefined): void
}

/**
 * Thumbs up / down for one option: a sorting aid, not a vote. Pressing the active thumb again clears it. Rendered
 * outside the vote row's <label>, so a press never reaches the vote button.
 */
export const OptionRatingButtons: FC<OptionRatingButtonsProps> = props => {
  const { option, rating, onChange, children } = props
  const { entity } = graphHooks.useEntity(option.entityRef)
  const label = getOptionLabel(option, entity?.name)

  const handleRate = (value: OptionRating) => () => onChange(rating === value ? undefined : value)

  return (
    <div role="group" aria-label={`Sort and comment on ${label}`} className={css.rating}>
      <IconButton
        size="small"
        aria-label="Thumbs up"
        aria-pressed={rating === 'up'}
        title="Thumbs up: sort to the top"
        className={rating === 'up' ? css.up : css.idle}
        onClick={handleRate('up')}
      >
        <ThumbsUp />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Thumbs down"
        aria-pressed={rating === 'down'}
        title="Thumbs down: sort to the bottom"
        className={rating === 'down' ? css.down : css.idle}
        onClick={handleRate('down')}
      >
        <ThumbsDown />
      </IconButton>
      {children}
    </div>
  )
}

export default OptionRatingButtons
